/**
 * WebGPU port of the WebGL `SkyboxRenderer` (renderer/viewer/three/skyboxRenderer.ts).
 *
 * Same math, same scene-graph shape. Differences:
 *   - imports from 'three/webgpu' (isolated bundle)
 *   - uses `MeshBasicNodeMaterial` instead of `MeshBasicMaterial`
 *   - drops the DebugGui dep (not pulled into webgpu bundle)
 *   - drops the equirectangular-image branch for now (chunks-only milestone)
 *
 * Note on `Fog`: the WebGPU renderer reads `scene.fog` and translates classic
 * `Fog`/`FogExp2` to its node graph automatically — no FogNode required.
 */

import {
  Scene,
  Vector3,
  Color,
  Fog,
  Mesh,
  PlaneGeometry,
  MeshBasicNodeMaterial,
  DoubleSide,
} from 'three/webgpu'

export const DEFAULT_TEMPERATURE = 0.75

export class WebGPUSkyboxRenderer {
  private skyMesh: Mesh<PlaneGeometry, MeshBasicNodeMaterial> | null = null
  private voidMesh: Mesh<PlaneGeometry, MeshBasicNodeMaterial> | null = null

  private worldTime = 0
  private partialTicks = 0
  private viewDistance = 4
  private temperature = DEFAULT_TEMPERATURE
  private inWater = false
  private waterBreathing = false
  private fogBrightness = 0
  private prevFogBrightness = 0
  private readonly fogOrangeness = 0
  private readonly distanceFactor = 2.7
  private readonly brightnessAtPosition = 1

  constructor (private readonly scene: Scene, public defaultSkybox: boolean) {
    this.createGradientSky()
  }

  update (cameraPosition: { x: number; y: number; z: number }) {
    if (this.skyMesh) this.skyMesh.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z)
    if (this.voidMesh) this.voidMesh.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z)
  }

  updateTime (timeOfDay: number, partialTicks = 0) {
    this.worldTime = timeOfDay
    this.partialTicks = partialTicks
    this.updateSkyColors()
  }

  updateViewDistance (viewDistance: number) {
    this.viewDistance = viewDistance
    this.updateSkyColors()
  }

  updateTemperature (temperature: number) {
    this.temperature = temperature
    this.updateSkyColors()
  }

  updateWaterState (inWater: boolean, waterBreathing: boolean) {
    this.inWater = inWater
    this.waterBreathing = waterBreathing
    this.updateSkyColors()
  }

  updateDefaultSkybox (defaultSkybox: boolean) {
    this.defaultSkybox = defaultSkybox
    this.updateSkyColors()
  }

  private createGradientSky () {
    const size = 64
    const scale = 256 / size + 2

    {
      const geometry = new PlaneGeometry(size * scale * 2, size * scale * 2)
      geometry.rotateX(-Math.PI / 2)
      geometry.translate(0, 16, 0)
      const material = new MeshBasicNodeMaterial({ color: 0xff_ff_ff, side: DoubleSide, depthTest: false })
      this.skyMesh = new Mesh(geometry, material)
      this.scene.add(this.skyMesh)
    }

    {
      const geometry = new PlaneGeometry(size * scale * 2, size * scale * 2)
      geometry.rotateX(-Math.PI / 2)
      geometry.translate(0, -16, 0)
      const material = new MeshBasicNodeMaterial({ color: 0xff_ff_ff, side: DoubleSide, depthTest: false })
      this.voidMesh = new Mesh(geometry, material)
      this.scene.add(this.voidMesh)
    }

    this.updateSkyColors()
  }

  private getFogColor (partialTicks = 0): Vector3 {
    const angle = this.getCelestialAngle(partialTicks)
    let rotation = Math.cos(angle * Math.PI * 2) * 2 + 0.5
    rotation = Math.max(0, Math.min(1, rotation))
    let x = 0.752_941_2
    let y = 0.847_058_83
    let z = 1
    x *= (rotation * 0.94 + 0.06)
    y *= (rotation * 0.94 + 0.06)
    z *= (rotation * 0.91 + 0.09)
    return new Vector3(x, y, z)
  }

  private getSkyColor (x = 0, z = 0, partialTicks = 0): Vector3 {
    const angle = this.getCelestialAngle(partialTicks)
    let brightness = Math.cos(angle * 3.141_593 * 2) * 2 + 0.5
    if (brightness < 0) brightness = 0
    if (brightness > 1) brightness = 1
    const temperature = this.getTemperature(x, z)
    const rgb = this.getSkyColorByTemp(temperature)
    const red = ((rgb >> 16) & 0xff) / 255
    const green = ((rgb >> 8) & 0xff) / 255
    const blue = (rgb & 0xff) / 255
    return new Vector3(red * brightness, green * brightness, blue * brightness)
  }

  private calculateCelestialAngle (time: number, partialTicks: number): number {
    const modTime = (time % 24_000)
    let angle = (modTime + partialTicks) / 24_000 - 0.25
    if (angle < 0) angle++
    if (angle > 1) angle--
    angle = 1 - ((Math.cos(angle * Math.PI) + 1) / 2)
    return angle
  }

  private getCelestialAngle (partialTicks: number): number {
    return this.calculateCelestialAngle(this.worldTime, partialTicks)
  }

  private getTemperature (_x: number, _z: number): number {
    return this.temperature
  }

  private getSkyColorByTemp (temperature: number): number {
    temperature /= 3
    if (temperature < -1) temperature = -1
    if (temperature > 1) temperature = 1
    const baseHue = 0.622_222_2 - temperature * 0.05
    const orangeHue = 0.12
    const hue = this.fogOrangeness > 0
      ? baseHue + (orangeHue - baseHue) * this.fogOrangeness * 0.8
      : baseHue + this.fogOrangeness * 0.1
    const saturation = 0.5 + temperature * 0.1 + Math.abs(this.fogOrangeness) * 0.3
    const brightness = 1
    return this.hsbToRgb(hue, saturation, brightness)
  }

  private hsbToRgb (hue: number, saturation: number, brightness: number): number {
    let r = 0; let g = 0; let b = 0
    if (saturation === 0) {
      r = g = b = Math.floor(brightness * 255 + 0.5)
    } else {
      const h = (hue - Math.floor(hue)) * 6
      const f = h - Math.floor(h)
      const p = brightness * (1 - saturation)
      const q = brightness * (1 - saturation * f)
      const t = brightness * (1 - (saturation * (1 - f)))
      switch (Math.floor(h)) {
        case 0: r = Math.floor(brightness * 255 + 0.5); g = Math.floor(t * 255 + 0.5); b = Math.floor(p * 255 + 0.5); break
        case 1: r = Math.floor(q * 255 + 0.5); g = Math.floor(brightness * 255 + 0.5); b = Math.floor(p * 255 + 0.5); break
        case 2: r = Math.floor(p * 255 + 0.5); g = Math.floor(brightness * 255 + 0.5); b = Math.floor(t * 255 + 0.5); break
        case 3: r = Math.floor(p * 255 + 0.5); g = Math.floor(q * 255 + 0.5); b = Math.floor(brightness * 255 + 0.5); break
        case 4: r = Math.floor(t * 255 + 0.5); g = Math.floor(p * 255 + 0.5); b = Math.floor(brightness * 255 + 0.5); break
        case 5: r = Math.floor(brightness * 255 + 0.5); g = Math.floor(p * 255 + 0.5); b = Math.floor(q * 255 + 0.5); break
      }
    }
    return 0xff_00_00_00 | (r << 16) | (g << 8) | (Math.trunc(b))
  }

  private updateSkyColors () {
    if (!this.skyMesh || !this.voidMesh) return

    if (!this.defaultSkybox) {
      this.skyMesh.visible = false
      this.voidMesh.visible = false
      return
    }

    this.skyMesh.visible = true
    this.voidMesh.visible = true

    this.prevFogBrightness = this.fogBrightness
    const renderDistance = this.viewDistance / 32
    const targetBrightness = this.brightnessAtPosition * (1 - renderDistance) + renderDistance
    this.fogBrightness += (targetBrightness - this.fogBrightness) * 0.1

    if (this.inWater) {
      const waterViewDistance = this.waterBreathing ? 100 : 5
      this.scene.fog = new Fog(new Color(0, 0, 1), 0.0025, waterViewDistance)
      this.scene.background = new Color(0, 0, 1)
      this.skyMesh.material.color.set(new Color(0, 0, 1))
      this.voidMesh.material.color.set(new Color(0, 0, 0.6))
      return
    }

    const viewDistance = this.viewDistance * 16
    const viewFactor = 1 - (0.25 + 0.75 * this.viewDistance / 32) ** 0.25

    const skyColor = this.getSkyColor(0, 0, this.partialTicks)
    const fogColor = this.getFogColor(this.partialTicks)

    const angle = this.getCelestialAngle(this.partialTicks)
    const brightness = Math.cos(angle * Math.PI * 2) * 2 + 0.5
    const clampedBrightness = Math.max(0, Math.min(1, brightness))

    const interpolatedBrightness = this.prevFogBrightness + (this.fogBrightness - this.prevFogBrightness) * this.partialTicks

    const red = (fogColor.x + (skyColor.x - fogColor.x) * viewFactor) * clampedBrightness * interpolatedBrightness
    const green = (fogColor.y + (skyColor.y - fogColor.y) * viewFactor) * clampedBrightness * interpolatedBrightness
    const blue = (fogColor.z + (skyColor.z - fogColor.z) * viewFactor) * clampedBrightness * interpolatedBrightness

    this.scene.background = new Color(red, green, blue)
    this.scene.fog = new Fog(new Color(red, green, blue), 0.0025, viewDistance * this.distanceFactor)

    this.skyMesh.material.color.set(new Color(skyColor.x, skyColor.y, skyColor.z))
    this.voidMesh.material.color.set(new Color(
      skyColor.x * 0.2 + 0.04,
      skyColor.y * 0.2 + 0.04,
      skyColor.z * 0.6 + 0.1
    ))
  }

  dispose () {
    if (this.skyMesh) {
      this.skyMesh.geometry.dispose()
      this.skyMesh.material.dispose()
      this.scene.remove(this.skyMesh)
      this.skyMesh = null
    }
    if (this.voidMesh) {
      this.voidMesh.geometry.dispose()
      this.voidMesh.material.dispose()
      this.scene.remove(this.voidMesh)
      this.voidMesh = null
    }
  }
}
